import { useEffect, useState } from "react";
import { CalendarClock, Facebook, Instagram, Loader2, Pencil, XCircle, CheckCircle2, Clock, Ban, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ScheduleEditDialog from "@/components/admin/ScheduleEditDialog";
import ContentCalendar from "@/components/admin/ContentCalendar";

const STATUS = {
  pending: { label: "Terjadwal", color: "#D97706", icon: Clock },
  processing: { label: "Memposting", color: "#3B82F6", icon: Loader2 },
  posting: { label: "Memposting", color: "#3B82F6", icon: Loader2 },
  posted: { label: "Terposting", color: "#059669", icon: CheckCircle2 },
  sent: { label: "Terposting", color: "#059669", icon: CheckCircle2 },
  failed: { label: "Gagal", color: "#E11D48", icon: XCircle },
  cancelled: { label: "Dibatalkan", color: "#64748B", icon: Ban },
};

const fmt = (iso) => new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" });

const StatusBadge = ({ status, testid }) => {
  const s = STATUS[status] || STATUS.pending;
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full" style={{ color: s.color, background: `${s.color}14` }}>
      <s.icon size={12} /> {s.label}
    </span>
  );
};

const Platforms = ({ list }) => (
  <span className="flex gap-1.5">
    {(list || []).map((t) => t === "facebook"
      ? <span key={t} className="flex items-center gap-1 text-xs text-[#1877F2]"><Facebook size={13} /> Facebook</span>
      : <span key={t} className="flex items-center gap-1 text-xs text-[#E1306C]"><Instagram size={13} /> Instagram</span>)}
  </span>
);

const PostRow = ({ p, bizName, canEdit, onEdit, onCancel }) => {
  const editable = canEdit && p.status === "pending" && new Date(p.send_at) > new Date();
  return (
    <tr className="border-t border-[#E2E8F0]" data-testid={`scheduled-row-${p.id}`}>
      <td className="px-4 py-3">
        <p className="text-sm font-medium text-slate-800 truncate max-w-[260px]" data-testid={`scheduled-title-${p.id}`}>{p.title || p.text.slice(0, 60)}</p>
        <p className="text-[11px] text-slate-500">{bizName}{p.video_url ? " · Video" : (p.image_urls || []).length ? ` · ${p.image_urls.length} gambar` : ""}</p>
      </td>
      <td className="px-4 py-3"><Platforms list={p.auto_social} /></td>
      <td className="px-4 py-3 text-xs text-slate-700" data-testid={`scheduled-time-${p.id}`}>{fmt(p.send_at)}</td>
      <td className="px-4 py-3"><StatusBadge status={p.status} testid={`scheduled-status-${p.id}`} /></td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        {editable && (
          <>
            <Button size="sm" variant="outline" onClick={() => onEdit(p)} data-testid={`scheduled-edit-${p.id}`} className="h-8 rounded-lg text-xs mr-1.5"><Pencil size={12} className="mr-1" /> Ubah</Button>
            <Button size="sm" variant="outline" onClick={() => onCancel(p)} data-testid={`scheduled-cancel-${p.id}`} className="h-8 rounded-lg text-xs text-[#E11D48] border-[#FECDD3] hover:bg-[#FFF1F2]"><Ban size={12} className="mr-1" /> Batalkan</Button>
          </>
        )}
        {p.status === "failed" && <span className="text-[11px] text-[#E11D48] max-w-[200px] inline-block truncate" title={p.detail}>{p.detail}</span>}
      </td>
    </tr>
  );
};

const Table = ({ children, empty, testid }) => (
  <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl overflow-x-auto" data-testid={testid}>
    <table className="w-full text-left">
      <thead className="bg-[#FFFFFF] text-[11px] uppercase tracking-wider text-slate-500">
        <tr><th className="px-4 py-2.5">Judul</th><th className="px-4 py-2.5">Platform</th><th className="px-4 py-2.5">Waktu Posting</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5" /></tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
    {empty && <p className="text-sm text-slate-500 text-center py-10">{empty}</p>}
  </div>
);

export default function ScheduledContent({ businesses = [], role }) {
  const [posts, setPosts] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const canEdit = role !== "operator";
  const bizName = (id) => businesses.find((b) => b.id === id)?.name || "-";

  const load = async () => {
    try {
      const [s, h] = await Promise.all([api.get("/scheduled-posts", { params: { social: true } }), api.get("/social/history", { params: { limit: 100 } })]);
      setPosts(s.data || []);
      setHistory(h.data || []);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); const timer = setInterval(load, 30000); return () => clearInterval(timer); }, []);

  const cancel = async (p) => {
    if (!window.confirm(`Batalkan jadwal "${p.title || "konten"}"?`)) return;
    try { await api.post(`/scheduled-posts/${p.id}/cancel`); toast.success("Jadwal dibatalkan"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <div className="text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Memuat...</div>;

  const upcoming = posts.filter((p) => ["pending", "posting", "processing"].includes(p.status));
  const others = posts.filter((p) => !["pending", "posting", "processing"].includes(p.status)).reverse();

  return (
    <div className="space-y-5" data-testid="scheduled-content-page">
      <div className="flex items-center gap-3">
        <CalendarClock size={20} className="text-[#2563EB]" />
        <div className="flex-1">
          <h2 className="font-heading font-bold">Konten Terjadwal</h2>
          <p className="text-xs text-slate-500">Konten Facebook &amp; Instagram yang akan diposting otomatis sesuai jadwal.</p>
        </div>
        <span className="text-xs bg-[#FEF3C7] text-[#B45309] px-2.5 py-1 rounded-full font-medium" data-testid="scheduled-count">{upcoming.length} terjadwal</span>
        <Button variant="outline" size="sm" onClick={load} data-testid="scheduled-refresh" className="rounded-xl h-8"><RefreshCw size={13} /></Button>
      </div>
      <Tabs defaultValue="upcoming">
        <TabsList className="bg-[#F1F5F9] h-auto flex-wrap">
          <TabsTrigger value="upcoming" data-testid="scheduled-tab-upcoming">Belum Diposting</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="scheduled-tab-calendar">Kalender</TabsTrigger>
          <TabsTrigger value="done" data-testid="scheduled-tab-done">Selesai / Dibatalkan</TabsTrigger>
          <TabsTrigger value="history" data-testid="scheduled-tab-history">Riwayat Posting</TabsTrigger>
        </TabsList>
        <TabsContent value="upcoming" className="mt-4">
          <Table testid="scheduled-upcoming-table" empty={!upcoming.length && "Belum ada konten terjadwal — buat dari halaman Generate Konten → Jadwalkan."}>
            {upcoming.map((p) => <PostRow key={p.id} p={p} bizName={bizName(p.business_id)} canEdit={canEdit} onEdit={setEditing} onCancel={cancel} />)}
          </Table>
        </TabsContent>
        <TabsContent value="calendar" className="mt-4">
          <ContentCalendar posts={posts} canEdit={canEdit} onChanged={load} onEdit={setEditing} />
        </TabsContent>
        <TabsContent value="done" className="mt-4">
          <Table testid="scheduled-done-table" empty={!others.length && "Belum ada data."}>
            {others.map((p) => <PostRow key={p.id} p={p} bizName={bizName(p.business_id)} canEdit={false} />)}
          </Table>
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl divide-y divide-[#E2E8F0]" data-testid="social-history-list">
            {!history.length && <p className="text-sm text-slate-500 text-center py-10">Belum ada riwayat posting.</p>}
            {history.map((h) => (
              <div key={h.id} className="px-4 py-3 flex items-start gap-3" data-testid={`social-history-${h.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{h.title || (h.text || "").slice(0, 60)}</p>
                  <p className="text-[11px] text-slate-500">{bizName(h.business_id)} · {fmt(h.created_at)} ·{h.video_url ? " Video ·" : ""}{h.status === "processing" ? " Diproses ·" : ""} {h.by_user === "autopilot" ? "Otomatis (terjadwal)" : `oleh ${h.by_user || "-"}`}</p>
                </div>
                <div className="flex gap-1.5">
                  {Object.entries(h.results || {}).map(([k, r]) => (
                    <span key={k} title={r.error || ""} className={`text-[11px] px-2 py-0.5 rounded-full ${r.ok ? "bg-[#ECFDF5] text-[#059669]" : "bg-[#FFF1F2] text-[#E11D48]"}`}>
                      {k} {r.ok ? "✓" : "✗"}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
      <ScheduleEditDialog post={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}
